import { showMessage } from "../../shared/ui/message-helper";
import { Config } from "../../services/config";

let cachedToken: string | null = null;
let cachedExpSeconds = 0;
let inFlight: Promise<string> | null = null;

type JwtPayload = { exp?: number };

/** Base64url decode with padding fix */
function base64UrlDecode(input: string): string {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "===".slice((normalized.length + 3) % 4);
  return atob(padded);
}

function parseJwtExp(token: string): number | undefined {
  try {
    const [, payloadB64] = token.split(".");
    const payloadJson = base64UrlDecode(payloadB64);
    const payload = JSON.parse(payloadJson) as JwtPayload;
    return payload.exp;
  } catch {
    return undefined;
  }
}

export function clearTokenCache(): void {
  cachedToken = null;
  cachedExpSeconds = 0;
}

/**
 * Get the current user's SSO token using ONLY Office.auth.getAccessToken (Promise API).
 * Includes in-memory caching and single-flight protection.
 */
export async function getUserToken(forceRefresh = false): Promise<string> {
  const nowSeconds = Math.floor(Date.now() / 1000);

  // Use valid cached token if available
  if (!forceRefresh && cachedToken) {
    const stillValid = nowSeconds < (cachedExpSeconds || 0) - Config.AUTH.REFRESH_BUFFER_SECONDS;
    if (stillValid) return cachedToken;
  }

  // If a request is already in-flight, await it
  if (inFlight) return inFlight;

  // Start single-flight
  inFlight = (async () => {
    try {
      // Guard: ensure Promise API is available (no fallback)
      if (typeof Office?.auth?.getAccessToken !== "function") {
        throw new Error("Office.auth.getAccessToken is not available in this host/version.");
      }

      // Always use a fresh options literal (prevents mutation from elsewhere)
      const options: Office.AuthOptions = {
        allowSignInPrompt: true,
        allowConsentPrompt: true,
        forMSGraphAccess: true
      };

      // Defensive: strip any rogue 'callback' prop (shouldn't exist, but just in case)
      if (Object.prototype.hasOwnProperty.call(options, "callback")) {
        delete (options as any).callback;
      }

      // First attempt
      let token = await Office.auth.getAccessToken(options);

      // Retry once on common throttling codes (13013)
      // Some hosts throw an error object with .code; others put it in the message.
      const assignToken = (t: string) => {
        const exp = parseJwtExp(t);
        const now = Math.floor(Date.now() / 1000);
        cachedToken = t;
        cachedExpSeconds = exp ?? (now + Config.AUTH.DEFAULT_LIFETIME_SECONDS);
      };

      assignToken(token);
      return token;

    } catch (error: any) {
      const code = error?.code ?? error?.message ?? "";
      if (String(code).includes("13013") || code === 13013 || code === 13) {
        // Brief jitter then retry once
        await new Promise(r => setTimeout(r, 500 + Math.random() * 500));

        const options: Office.AuthOptions = {
          allowSignInPrompt: true,
          allowConsentPrompt: true,
          forMSGraphAccess: true
        };
        if (Object.prototype.hasOwnProperty.call(options, "callback")) {
          delete (options as any).callback;
        }

        const token = await Office.auth.getAccessToken(options);
        const exp = parseJwtExp(token);
        const now = Math.floor(Date.now() / 1000);
        cachedToken = token;
        cachedExpSeconds = exp ?? (now + Config.AUTH.DEFAULT_LIFETIME_SECONDS);
        return token;
      }

      throw error;
    } finally {
      inFlight = null;
    }
  })();

  try {
    return await inFlight;
  } catch (exception) {
    clearTokenCache();
    showMessage("EXCEPTION: " + (typeof exception === "string" ? exception : JSON.stringify(exception)));
    throw exception;
  }
}